// SF4 — Average Fulfilment Time.
//
// Never blended into one figure. The seller controls dispatch on the channels
// they ship themselves and not on the ones Amazon ships, so an average across
// them would describe nobody's performance. The split is the metric.
//
// Only one channel is measured. Amazon-fulfilled shipments carry a real
// despatch timestamp; merchant despatch appears in no report, so those times
// are estimated and say so. Their mean is deliberately withheld — the estimate
// runs late in about a fifth of cases and only in one direction, so a mean
// would make the seller look slower than they are. Medians only.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getFulfilmentTime } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

// Hours read badly past a day, so anything longer is said in days.
const duration = (hours) => {
  if (hours === null || hours === undefined) return <Blank />;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

// Faster is better here, so a fall is the good direction.
const Trend = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const slower = value > 0;
  return (
    <span className={slower ? local.down : local.up}>
      {slower ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

const FulfilmentTime = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getFulfilmentTime(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const channels = data?.channels || [];
  const products = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Time to dispatch</h2>
      </div>
      <p className={local.muted}>
        From the order being placed to it leaving, by shipment type. These are
        never combined into one number — you control some of these channels and
        not others.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load dispatch times'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && data && (
        <>
          <div className={local.actorRow}>
            {channels.map((channel) => (
              <div key={channel.channel}
                className={`${local.actorTile} ${channel.quality === 'measured' ? local.measured : local.estimated}`}>
                <div className={local.todayLabel}>
                  {channel.channel}
                  {channel.quality === 'estimated' && (
                    <span className={local.estTag} title="No despatch timestamp exists for this channel">
                      estimated
                    </span>
                  )}
                </div>
                <div className={local.todayValue}>{duration(channel.median_hours)}</div>
                <div className={local.todayFoot}>
                  typical, across {count(channel.orders)} orders
                  {' · '}<Trend value={channel.trend} />
                </div>
                <div className={local.todayFoot}>
                  {channel.mean_hours !== null && channel.mean_hours !== undefined
                    ? <>average {duration(channel.mean_hours)} · slowest tenth {duration(channel.p90_hours)}</>
                    : <>slowest tenth {duration(channel.p90_hours)} · average withheld, the estimate only errs late</>}
                </div>
              </div>
            ))}
          </div>

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Slowest products to leave
              <span className={local.groupCount}>
                {count(data.suppressed_products)} below {data.min_orders_for_ranking} orders not ranked
              </span>
            </h3>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th className={local.numeric}>Orders</th>
                  <th className={local.numeric}>Typical</th>
                  <th className={local.numeric}>Slowest tenth</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={5}>
                      No product reached {data.min_orders_for_ranking} dispatched
                      orders in this period.
                    </td>
                  </tr>
                ) : products.map((row) => (
                  <tr key={row.asin}>
                    <td className={local.drillId}>{row.asin}</td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                    <td className={local.numeric}>{duration(row.median_hours)}</td>
                    <td className={local.numeric}>{duration(row.p90_hours)}</td>
                    <td>
                      <span className={row.quality === 'measured' ? local.measuredTag : local.estTag}>
                        {row.quality}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default FulfilmentTime;
